import { onlyNumbers } from '@/std/format';
import { TipoPessoa } from '@/types/enum';

function sameDigit(value: string): boolean {
    const first = value[0];

    if (!first) {
        return false;
    }

    for (let i = 1; i < value.length; i += 1) {
        if (value[i] !== first) {
            return false;
        }
    }

    return true;
}

declare const validCnpjBrand: unique symbol;

export type ValidCnpj = string & {
    readonly [validCnpjBrand]: true;
};

/**
 * https://servicos.receitafederal.gov.br/servico/cnpj-alfa/simular
 * Valida CNPJ numérico ou alfanumérico.
 *
 * O CNPJ deve possuir:
 * - 12 caracteres alfanuméricos;
 * - 2 dígitos verificadores numéricos.
 */
export function parseCNPJ(value: string | undefined | null):
    | {
          valid: true;
          cnpj: ValidCnpj;
      }
    | {
          valid: false;
      } {
    if (!value) {
        return { valid: false };
    }

    const cnpj = value.trim().toUpperCase().replace(/[./-]/g, '');

    // Os 12 primeiros caracteres podem ser A-Z ou 0-9.
    // Os dois últimos devem ser obrigatoriamente numéricos.
    if (!/^[A-Z0-9]{12}\d{2}$/.test(cnpj)) {
        return { valid: false };
    }

    // Evita CNPJs numéricos formados por um único dígito repetido.
    if (/^(\d)\1{13}$/.test(cnpj)) {
        return { valid: false };
    }

    const base = cnpj.slice(0, 12);
    const informedDigits = cnpj.slice(12);

    const calculateDigit = (characters: string): number => {
        let weight = 2;
        let sum = 0;

        for (let index = characters.length - 1; index >= 0; index--) {
            /*
             * Conforme a regra do CNPJ alfanumérico:
             * valor para cálculo = código ASCII - 48
             *
             * 0 -> 0
             * 9 -> 9
             * A -> 17
             * Z -> 42
             */
            const characterValue = characters.charCodeAt(index) - 48;

            sum += characterValue * weight;

            weight = weight === 9 ? 2 : weight + 1;
        }

        const remainder = sum % 11;

        return remainder === 0 || remainder === 1 ? 0 : 11 - remainder;
    };

    const firstDigit = calculateDigit(base);
    const secondDigit = calculateDigit(`${base}${firstDigit}`);
    const valid = informedDigits === `${firstDigit}${secondDigit}`;

    return valid ? { cnpj: cnpj as ValidCnpj, valid: true } : { valid: false };
}

/** Verifica se o valor informado é um CNPJ numérico ou alfanumérico válido. */
export function isCNPJ(value: string | undefined | null): boolean {
    return parseCNPJ(value).valid;
}

export function isCPF(value: string | null | undefined): boolean {
    const input = onlyNumbers(value);

    // Se todos digitos forem iguais é invalido
    if (sameDigit(input)) {
        return false;
    }

    let soma = 0;

    for (let i = 1; i <= 9; i += 1) {
        soma += Number.parseInt(input.substring(i - 1, i), 10) * (11 - i);
    }

    let resto = (soma * 10) % 11;

    if (resto === 10 || resto === 11) {
        resto = 0;
    }

    if (resto !== Number.parseInt(input.substring(9, 10), 10)) {
        return false;
    }

    soma = 0;

    for (let i = 1; i <= 10; i += 1) {
        soma += Number.parseInt(input.substring(i - 1, i), 10) * (12 - i);
    }

    resto = (soma * 10) % 11;

    if (resto === 10 || resto === 11) {
        resto = 0;
    }

    if (resto !== Number.parseInt(input.substring(10, 11), 10)) {
        return false;
    }

    return true;
}

export function validatorDocumento(
    value: unknown,
    required: boolean,
    tipoPessoa: TipoPessoa | null | undefined,
): Promise<void> {
    const error = new Error('Informe um documento válido');

    if (!value || typeof value !== 'string') {
        return required ? Promise.reject(error) : Promise.resolve();
    }

    if (!tipoPessoa) {
        return isCPF(value) || isCNPJ(value) ? Promise.resolve() : Promise.reject(error);
    }

    if (tipoPessoa === TipoPessoa.Fisica) {
        return isCPF(value) ? Promise.resolve() : Promise.reject(error);
    }

    if (tipoPessoa === TipoPessoa.Juridica) {
        return isCNPJ(value) ? Promise.resolve() : Promise.reject(error);
    }

    // Estrangeiro
    return Promise.resolve();
}

const regexEmail =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function isEmail(value: string | null | undefined): boolean {
    if (!value) {
        return false;
    }

    return regexEmail.test(value);
}

export function isTelefone(value: string | number | null | undefined): boolean {
    if (!value) {
        return false;
    }

    const v1 = typeof value === 'string' ? value : value.toString();
    const v2 = v1.trim();
    return v2.length > 9 && v2.length < 12;
}

export function isCep(value: string | null | undefined): boolean {
    return onlyNumbers(value).length === 8;
}

export function validatorCep(value: unknown, required: boolean): Promise<void> {
    if (!(value || required)) {
        return Promise.resolve();
    }

    if (typeof value === 'string' && isCep(value)) {
        return Promise.resolve();
    }

    return Promise.reject(new Error('Informe um cep válido'));
}

export function validateImage(file: File, size: number): boolean {
    const isLt2M = file.size / 1024 / 1024 < size;
    return isLt2M;
}

export function isChaveAcesso(value: string | null | undefined): boolean {
    // se precisar de mais validações: https://wiki.centrium.com.br/nota-fiscal/validacao-da-chave-de-acesso-na-nota-de-entrada/
    return value?.length === 44;
}

const regexDate = /\d{1,2}\/\d{1,2}\/\d{2,4}/;

export function isDate(value: string | null | undefined): boolean {
    if (!value) {
        return false;
    }

    return regexDate.test(value);
}

export const passwordRules = [
    {
        message: 'Por favor, informe uma senha',
        required: true,
    },
    {
        message: 'A senha deve ter no mínimo 8 caracteres',
        min: 8,
    },
    {
        message: 'A senha deve ter pelo menos uma letra maiúscula',
        pattern: /[A-Z]/,
    },
    {
        message: 'A senha deve ter pelo menos uma letra minúscula',
        pattern: /[a-z]/,
    },
    {
        message: 'A senha deve ter pelo menos um número',
        pattern: /[0-9]/,
    },
    {
        message: 'A senha deve ter pelo menos um caractere especial',
        pattern: /[!@#$%^&*(),.?":{}|<>]/,
    },
];
